import React from 'react';
import { Clock, Image, MapPin, Calendar, Trash2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели с недавно просмотренными файлами
 */
const RecentFilesSidebar = ({ recentFiles, onFileSelect }) => {
  if (!recentFiles || recentFiles.length === 0) {
    return null;
  }

  // Функция для форматирования размера файла
  const formatFileSize = (sizeInBytes) => {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} Б`;
    } else if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(1)} КБ`;
    } else {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} МБ`;
    }
  };

  // Функция для форматирования даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    // Сегодня
    if (date.toDateString() === now.toDateString()) {
      return `Сегодня, ${date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    // Вчера
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return `Вчера, ${date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    // В течение последней недели
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    if (date > oneWeekAgo) {
      const options = { weekday: 'long', hour: '2-digit', minute: '2-digit' };
      return date.toLocaleString('ru-RU', options);
    }
    
    // Более старая дата
    return date.toLocaleString('ru-RU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Обработчик выбора файла
  const handleFileSelect = (file) => {
    fetch(file.url)
      .then(response => response.blob())
      .then(blob => {
        const newFile = new File([blob], file.name, { type: file.type });
        onFileSelect(newFile);
      })
      .catch(error => {
        console.error('Ошибка при загрузке файла:', error);
        alert('Не удалось загрузить выбранный файл. Попробуйте загрузить его заново.');
      });
  };

  // Обработчик очистки истории просмотров
  const clearHistory = () => {
    localStorage.removeItem('exifRecentFiles');
    window.location.reload(); // Перезагрузка страницы для обновления состояния
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Clock className="w-5 h-5 mr-2 text-purple-500" />
          Недавние файлы
        </h3>
        {recentFiles.length > 0 && (
          <Button
            onClick={clearHistory}
            variant="ghost"
            size="xs"
            icon={<Trash2 className="w-4 h-4" />}
            title="Очистить историю"
          >
            Очистить
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {recentFiles.map((file, index) => (
          <div 
            key={index} 
            className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
            onClick={() => handleFileSelect(file)}
          >
            {/* Миниатюра */}
            <div className="w-12 h-12 rounded overflow-hidden bg-white dark:bg-gray-600 flex-shrink-0">
              <img 
                src={file.thumbnail} 
                alt={file.name} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null; 
                  e.target.src = "/placeholder.png";
                }}
              />
            </div>
            
            {/* Информация о файле */}
            <div className="flex-grow min-w-0">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1 truncate">
                {file.name}
              </h4>
              <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 space-x-3">
                <span className="flex items-center">
                  <Image className="w-3 h-3 mr-1" />
                  {formatFileSize(file.size)}
                </span>
                {file.exifPreview && (
                  <>
                    {file.exifPreview.camera && (
                      <span className="hidden sm:flex items-center truncate">
                        <Camera className="w-3 h-3 mr-1" />
                        {file.exifPreview.camera}
                      </span>
                    )}
                    {file.exifPreview.hasLocation && (
                      <span className="flex items-center text-red-500 dark:text-red-400">
                        <MapPin className="w-3 h-3 mr-1" />
                        GPS
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center">
                <Calendar className="w-3 h-3 mr-1" />
                {formatDate(file.date)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {recentFiles.length > 0 && (
        <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          <p>История сохраняется только в вашем браузере</p>
        </div>
      )}
    </div>
  );
};

// Вспомогательная компонента для иконки камеры
const Camera = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path>
    <circle cx="12" cy="13" r="3"></circle>
  </svg>
);

export default RecentFilesSidebar;